import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api, errText } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import {
  Activity, RefreshCw, Users, Church, Wallet, KeySquare, MessageSquare,
  AlertOctagon, ShieldAlert, Server,
} from 'lucide-react';
import { format } from 'date-fns';
import { StatCard, Banner, StatusPill, Loading, fmtDate, ChartBox } from '@/components/OpsUI';
import LiveFeed from '@/components/LiveFeed';

export default function AppMonitorPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState(null);
  const [errors, setErrors] = useState(null);
  const [lockouts, setLockouts] = useState(null);
  const [connError, setConnError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const st = await api.get('/ops/status');
      setStatus(st.data);
      setConnError(st.data?.ok ? null : (typeof st.data?.detail === 'string' ? st.data.detail : 'Sin conexión'));
    } catch (e) {
      setConnError(errText(e, 'Error de conexión'));
    }
    const safe = async (url, setter, params) => {
      try { const { data } = await api.get(url, { params }); setter(data); }
      catch (e) { /* individual failures ignored */ }
    };
    await Promise.all([
      safe('/ops/metrics/summary', setSummary),
      safe('/ops/metrics/timeseries', setSeries, { hours: 24 }),
      safe('/ops/errors', setErrors, { limit: 20, source: 'db' }),
      safe('/ops/security/lockouts', setLockouts),
    ]);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const healthy = status?.ok;
  const services = status?.data?.services || {};
  const points = (series?.points || []).map((p) => ({
    label: p.hour ? format(new Date(p.hour), 'HH:mm') : '',
    requests: p.requests || 0,
    errors_5xx: p.errors_5xx || 0,
    avg_latency_ms: p.avg_latency_ms || 0,
    p95_ms: p.p95_ms || 0,
  }));

  return (
    <div className="space-y-6" data-testid="app-monitor-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
            <Activity className="h-6 w-6 text-[hsl(var(--primary))]" /> {t('nav_app_monitor')}
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 font-mono">
            {status?.base_url || '—'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StatusPill status={healthy ? t('ops_connected') : t('ops_disconnected')} />
          <Button variant="outline" size="sm" onClick={load} data-testid="app-monitor-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> {t('refresh')}
          </Button>
        </div>
      </div>

      {connError && (
        <Banner kind="error" title={t('ops_disconnected')} message={String(connError)} />
      )}

      {loading ? <Loading label={t('loading')} /> : (
        <>
          {/* Dependencias */}
          <Card className="p-4 bg-card border border-border">
            <div className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-3 flex items-center gap-2">
              <Server className="h-4 w-4" /> {t('ops_dependencies')}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {Object.entries(services).map(([name, info]) => (
                <div key={name} className="p-3 rounded-lg border border-border bg-[hsl(var(--muted))]" data-testid={`ops-dep-${name}`}>
                  <div className="text-xs font-medium capitalize mb-1.5">{name}</div>
                  <StatusPill status={info?.status} />
                  {info?.latency_ms != null && (
                    <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1 font-mono">{Math.round(info.latency_ms)}ms</div>
                  )}
                </div>
              ))}
              {Object.keys(services).length === 0 && (
                <div className="text-sm text-[hsl(var(--muted-foreground))]">{t('no_data')}</div>
              )}
            </div>
          </Card>

          {/* KPIs */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <StatCard icon={Users} label={t('ops_users_total')} value={summary.users?.total ?? '—'} sub={`${summary.users?.pending_approval ?? 0} ${t('ops_pending_approval')}`} />
              <StatCard icon={Church} label={t('ops_churches')} value={summary.churches?.total ?? '—'} sub={`${summary.churches?.provisioned ?? 0} provisioned`} />
              <StatCard icon={Wallet} label={t('ops_contributions')} value={summary.contributions?.paid_24h_count ?? 0} sub={`${(summary.contributions?.paid_24h_ves ?? 0).toLocaleString()} VES / 24h`} />
              <StatCard icon={KeySquare} label={t('ops_sessions')} value={summary.sessions?.active_refresh_tokens ?? '—'} />
              <StatCard icon={MessageSquare} label={t('ops_chat')} value={summary.chat?.messages_total ?? '—'} />
              <StatCard icon={AlertOctagon} label={t('ops_errors_24h')} value={summary.errors?.unhandled_5xx_24h ?? 0} accent={(summary.errors?.unhandled_5xx_24h ?? 0) > 0 ? 'text-red-400' : 'text-emerald-400'} />
            </div>
          )}

          {/* Feed en vivo + Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-1">
              <LiveFeed />
            </div>
            <Card className="lg:col-span-1 p-4 bg-card border border-border">
              <div className="text-sm font-display font-semibold mb-3">{t('ops_requests_trend')}</div>
              <ChartBox height={240} testid="ops-chart-traffic">
                  <AreaChart data={points} margin={{ left: -18, right: 12, top: 8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="req" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#00CCFF" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#00CCFF" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                    <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                    <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
                    <Area type="monotone" dataKey="requests" stroke="#00CCFF" strokeWidth={2} fill="url(#req)" />
                    <Line type="monotone" dataKey="errors_5xx" stroke="#f87171" strokeWidth={2} dot={false} />
                  </AreaChart>
                </ChartBox>
            </Card>
            <Card className="lg:col-span-1 p-4 bg-card border border-border">
              <div className="text-sm font-display font-semibold mb-3">{t('kpi_latency')} (ms)</div>
              <ChartBox height={240} testid="ops-chart-latency">
                  <LineChart data={points} margin={{ left: -18, right: 12, top: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                    <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                    <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
                    <Line type="monotone" dataKey="avg_latency_ms" name="avg" stroke="#00CCFF" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="p95_ms" name="p95" stroke="#056A84" strokeWidth={2} dot={false} />
                  </LineChart>
                </ChartBox>
            </Card>
          </div>

          {/* Errores + Lockouts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2 bg-card border border-border">
              <div className="p-4 border-b border-border text-sm font-display font-semibold flex items-center gap-2">
                <AlertOctagon className="h-4 w-4 text-red-400" /> {t('ops_recent_errors')}
              </div>
              <div className="max-h-[320px] overflow-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-card">
                    <tr className="text-left text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] border-b border-border">
                      <th className="px-3 py-2">status</th><th className="px-3 py-2">method</th><th className="px-3 py-2">path</th><th className="px-3 py-2">type</th><th className="px-3 py-2">when</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {(errors?.items || []).map((e) => (
                      <tr key={e.error_id} className="border-b border-border/40 hover:bg-accent/40">
                        <td className="px-3 py-2"><span className="text-red-300">{e.status}</span></td>
                        <td className="px-3 py-2">{e.method}</td>
                        <td className="px-3 py-2 truncate max-w-[220px]">{e.path}</td>
                        <td className="px-3 py-2">{e.exc_type}</td>
                        <td className="px-3 py-2 text-[hsl(var(--muted-foreground))]">{fmtDate(e.created_at)}</td>
                      </tr>
                    ))}
                    {(!errors?.items || errors.items.length === 0) && (
                      <tr><td colSpan={5} className="px-4 py-8 text-center text-[hsl(var(--muted-foreground))] font-sans">{t('no_data')}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
            <Card className="bg-card border border-border">
              <div className="p-4 border-b border-border text-sm font-display font-semibold flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 text-amber-400" /> {t('ops_lockouts')}
              </div>
              <div className="p-4 space-y-3 text-sm">
                <div className="text-xs text-[hsl(var(--muted-foreground))]">
                  Ventana {lockouts?.window_minutes ?? '—'} min · email≥{lockouts?.thresholds?.per_email ?? '—'} · ip≥{lockouts?.thresholds?.per_ip ?? '—'}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">Emails</div>
                  {(lockouts?.locked_emails || []).length === 0 ? <div className="text-xs text-emerald-300">{t('ops_no_lockouts')}</div> :
                    (lockouts.locked_emails).map((x, i) => <div key={i} className="font-mono text-xs">{typeof x === 'string' ? x : JSON.stringify(x)}</div>)}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">IPs</div>
                  {(lockouts?.locked_ips || []).length === 0 ? <div className="text-xs text-emerald-300">—</div> :
                    (lockouts.locked_ips).map((x, i) => <div key={i} className="font-mono text-xs">{typeof x === 'string' ? x : JSON.stringify(x)}</div>)}
                </div>
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
